import RapidXCommsRtc from './commsRtc';

/*
 * RapidX Comms RTC compatibility adapter.
 *
 * CommsPage owns WebSocket lifecycle and PTT floor control.
 * RapidXCommsRtc owns microphone, TURN and RTCPeerConnection state.
 */

class RapidXRtcClient {
  constructor() {
    this.rtc = null;
    this.liveSocket = null;
    this.channelId = null;

    /*
     * Browser audio players for incoming WebRTC streams,
     * keyed by remote peer connection ID.
     */
    this.remoteAudioElements = new Map();
  }

  async initialize({
    channelId,
    liveSocket,
  } = {}) {
    if (!channelId) {
      throw new Error(
        'RapidX RTC channel ID is required.'
      );
    }

    if (
      !liveSocket ||
      typeof liveSocket.send !== 'function'
    ) {
      throw new Error(
        'RapidX RTC live socket is unavailable.'
      );
    }

    /*
     * Re-initialisation must not leave an old microphone,
     * peer connection or TURN state alive.
     */
    this.destroy();

    this.channelId = channelId;
    this.liveSocket = liveSocket;

    this.rtc = new RapidXCommsRtc({
      sendSignal: (message) => {
        if (
          this.liveSocket &&
          typeof this.liveSocket.send === 'function'
        ) {
          this.liveSocket.send(message);
        }
      },

      onRemoteStream: (
        peerConnectionId,
        stream
      ) => {
        this.attachRemoteAudio(
          peerConnectionId,
          stream
        );
      },

      onError: (error) => {
        console.error(
          '[RapidXRtcClient]',
          error
        );
      },
    });

    this.rtc.setChannel(channelId);

    /*
     * Prepare TURN during channel initialisation.
     *
     * Do NOT acquire the microphone here. Mobile browsers may
     * reject getUserMedia() when this method is reached from an
     * asynchronous WebSocket event rather than a user gesture.
     *
     * Microphone acquisition is explicitly activated from the
     * PTT interaction via activateMicrophone().
     */
    await this.rtc.ensureTurnCredentials();

    return true;
  }

  async activateMicrophone() {
    this.assertReady();

    await this.rtc.ensureMicrophone();

    /*
     * PTT safety invariant:
     * acquiring permission must never start transmission.
     */
    this.rtc.setTransmitEnabled(false);

    return true;
  }

  async handlePeerJoined(
    message = {},
    { initiator = false } = {}
  ) {
    this.assertReady();

    const peerConnectionId =
      this.getSourceConnectionId(message);

    if (!peerConnectionId) {
      return;
    }

    /*
     * An existing channel participant is announced to the new
     * participant. The new participant initiates the connection.
     */
    await this.rtc.createPeer(
      peerConnectionId,
      {
        initiator: Boolean(initiator),
      }
    );
  }

  async handlePeerReady(message = {}) {
    this.assertReady();

    const peerConnectionId =
      this.getSourceConnectionId(message);

    if (!peerConnectionId) {
      return;
    }

    /*
     * A participant has announced RTC readiness.
     * Ensure we have a peer object without forcing a second
     * offer if one already exists.
     */
    await this.rtc.createPeer(
      peerConnectionId,
      {
        initiator: false,
      }
    );
  }

  async handleOffer(message = {}) {
    this.assertReady();

    const peerConnectionId =
      this.getSourceConnectionId(message);

    if (
      !peerConnectionId ||
      !message.description
    ) {
      return;
    }

    await this.rtc.handleOffer(
      peerConnectionId,
      message.description
    );
  }

  async handleAnswer(message = {}) {
    this.assertReady();

    const peerConnectionId =
      this.getSourceConnectionId(message);

    if (
      !peerConnectionId ||
      !message.description
    ) {
      return;
    }

    await this.rtc.handleAnswer(
      peerConnectionId,
      message.description
    );
  }

  async handleIceCandidate(message = {}) {
    this.assertReady();

    const peerConnectionId =
      this.getSourceConnectionId(message);

    if (!peerConnectionId) {
      return;
    }

    await this.rtc.handleIceCandidate(
      peerConnectionId,
      message.candidate ?? null
    );
  }

  attachRemoteAudio(
    peerConnectionId,
    stream
  ) {
    if (
      !peerConnectionId ||
      !stream
    ) {
      return;
    }

    let audio =
      this.remoteAudioElements.get(
        peerConnectionId
      );

    if (!audio) {
      audio = document.createElement('audio');

      audio.autoplay = true;
      audio.playsInline = true;
      audio.muted = false;
      audio.volume = 1;

      audio.setAttribute(
        'data-rapidx-peer',
        peerConnectionId
      );

      /*
       * The element does not need visible UI, but keeping it in
       * the DOM gives the browser a normal media playback target.
       */
      audio.style.display = 'none';

      document.body.appendChild(audio);

      this.remoteAudioElements.set(
        peerConnectionId,
        audio
      );
    }

    if (audio.srcObject !== stream) {
      audio.srcObject = stream;
    }

    const playPromise = audio.play();

    if (
      playPromise &&
      typeof playPromise.catch === 'function'
    ) {
      playPromise.catch((error) => {
        console.warn(
          '[RapidXRtcClient] remote audio playback blocked:',
          error
        );
      });
    }

    console.info(
      '[RapidXRtcClient] remote audio attached:',
      peerConnectionId
    );
  }

  cleanupRemoteAudio() {
    for (
      const audio of
      this.remoteAudioElements.values()
    ) {
      try {
        audio.pause();
        audio.srcObject = null;
        audio.remove();
      } catch (error) {
        console.warn(
          '[RapidXRtcClient] remote audio cleanup:',
          error
        );
      }
    }

    this.remoteAudioElements.clear();
  }

  setMicrophoneEnabled(enabled) {
    if (!this.rtc) {
      return;
    }

    this.rtc.setTransmitEnabled(
      Boolean(enabled)
    );
  }

  getSourceConnectionId(message = {}) {
    return (
      message.from_connection_id ||
      message.source_connection_id ||
      message.connection_id ||
      message.peer_connection_id ||
      null
    );
  }

  assertReady() {
    if (!this.rtc || !this.channelId) {
      throw new Error(
        'RapidX RTC client is not initialised.'
      );
    }
  }

  destroy() {
    if (this.rtc) {
      try {
        this.rtc.setTransmitEnabled(false);
      } catch {
        // Safety mute best effort.
      }

      try {
        this.rtc.reset();
      } catch (error) {
        console.warn(
          '[RapidXRtcClient] cleanup:',
          error
        );
      }
    }

    this.cleanupRemoteAudio();

    this.rtc = null;
    this.liveSocket = null;
    this.channelId = null;
  }
}

const rtcClient = new RapidXRtcClient();

export default rtcClient;
